import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, MapPin, Clock, Users } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import useSEO from '../hooks/useSEO';
import useJsonLd from '../hooks/useJsonLd';
import { SITE_CONFIG } from '../data/seoConfig';
import { CASE_STUDIES } from '../mock';
import { CASE_STUDY_DETAILS } from '../data/caseStudies';

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const cs = CASE_STUDIES.find((c) => c.slug === slug);
  const details = CASE_STUDY_DETAILS[slug];
  const canonical = cs ? `${SITE_CONFIG.url}/case-studies/${cs.slug}` : undefined;

  useSEO(
    cs
      ? {
          title: `${cs.title} Case Study — ${cs.metric} | Ransen`,
          description: `${cs.summary} Full case study, methodology, and results from Ransen.`,
          canonical,
          ogImage: cs.image,
          keywords: `${cs.industry.toLowerCase()} case study, digital marketing case study, ${cs.tags.join(', ').toLowerCase()}`,
        }
      : {}
  );

  useJsonLd(
    cs ? `case-study-schema-${cs.slug}` : 'case-study-schema',
    cs && details
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: `${cs.title} — ${cs.metric}`,
            description: details.heroDesc,
            image: cs.image,
            url: canonical,
            author: { '@id': `${SITE_CONFIG.url}/#organization` },
            publisher: { '@id': `${SITE_CONFIG.url}/#organization` },
            keywords: cs.tags.join(', '),
            about: cs.industry,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_CONFIG.url },
              { '@type': 'ListItem', position: 2, name: 'Case Studies', item: `${SITE_CONFIG.url}/case-studies` },
              { '@type': 'ListItem', position: 3, name: cs.title, item: canonical },
            ],
          },
        ]
      : null
  );

  if (!cs) {
    return (
      <div className='pt-40 pb-32 text-center'>
        <h1 className='text-3xl text-white'>Case study not found</h1>
        <Link to='/case-studies' className='mt-6 inline-block text-brand'>Back to case studies</Link>
      </div>
    );
  }

  const related = CASE_STUDIES.filter((c) => c.slug !== slug).slice(0, 3);
  const d = details || {};

  return (
    <div data-testid='case-study-detail-page'>
      <PageHeader
        eyebrow={`Case Study · ${cs.industry}`}
        title={<span className='text-brand-gradient'>{cs.title}</span>}
        description={cs.metric}
        crumbs={[{ label: 'Case Studies', to: '/case-studies' }, { label: cs.title }]}
      />

      <section className='py-10'>
        <div className='max-w-5xl mx-auto px-6'>
          {/* White paper case study container */}
          <article className='bg-white text-slate-900 rounded-2xl shadow-2xl shadow-black/40 ring-1 ring-black/5 overflow-hidden'>
            <div className='overflow-hidden'>
              <img src={cs.image} alt={cs.title} className='w-full h-auto' />
            </div>

            <div className='px-6 py-10 md:px-12 md:py-14'>
              {/* Client meta */}
              <div className='flex flex-wrap gap-x-6 gap-y-3 text-slate-500 text-[13px] mb-8 pb-6 border-b border-slate-100'>
                {d.client && (
                  <span className='inline-flex items-center gap-1.5'>
                    <Users className='w-3.5 h-3.5' /> {d.client}
                  </span>
                )}
                {d.region && (
                  <span className='inline-flex items-center gap-1.5'>
                    <MapPin className='w-3.5 h-3.5' /> {d.region}
                  </span>
                )}
                {d.duration && (
                  <span className='inline-flex items-center gap-1.5'>
                    <Clock className='w-3.5 h-3.5' /> {d.duration}
                  </span>
                )}
              </div>

              <h1 className='font-heading text-3xl md:text-4xl font-bold text-slate-900 leading-tight mb-4'>
                {cs.title}
              </h1>
              <div className='text-brand font-heading text-xl font-semibold mb-8'>{cs.metric}</div>

              {d.heroDesc && (
                <p className='text-slate-700 text-[17px] leading-[1.75] font-body mb-10 border-l-4 border-brand/60 pl-5'>
                  {d.heroDesc}
                </p>
              )}

              {/* Metrics grid */}
              {d.metrics && d.metrics.length > 0 && (
                <div className='grid grid-cols-2 md:grid-cols-4 gap-4 mb-12' data-testid='case-study-metrics'>
                  {d.metrics.map((m, i) => (
                    <div key={i} className='bg-slate-50 rounded-xl p-5 text-center border border-slate-100'>
                      <div className='text-brand font-heading text-2xl md:text-[26px] font-bold leading-tight'>{m.value}</div>
                      <div className='mt-1.5 text-slate-500 text-[12px] uppercase tracking-wider'>{m.label}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Challenge */}
              {d.challenge && d.challenge.length > 0 && (
                <section className='mb-10'>
                  <h2 className='font-heading text-2xl md:text-[28px] font-bold text-slate-900 mb-5'>The Challenge</h2>
                  <ul className='space-y-3'>
                    {d.challenge.map((c, i) => (
                      <li key={i} className='flex gap-3 text-slate-700 text-[16px] leading-[1.75] font-body'>
                        <div className='w-1.5 h-1.5 rounded-full bg-brand mt-2.5 shrink-0' />
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Solution */}
              {d.solution && d.solution.length > 0 && (
                <section className='mb-10'>
                  <h2 className='font-heading text-2xl md:text-[28px] font-bold text-slate-900 mb-5'>Our Approach</h2>
                  <ul className='space-y-3'>
                    {d.solution.map((c, i) => (
                      <li key={i} className='flex gap-3 text-slate-700 text-[16px] leading-[1.75] font-body'>
                        <Check className='w-4 h-4 text-brand mt-1.5 shrink-0' />
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Results */}
              {d.results && d.results.length > 0 && (
                <section className='mb-10'>
                  <h2 className='font-heading text-2xl md:text-[28px] font-bold text-slate-900 mb-5'>The Results</h2>
                  <ul className='space-y-3'>
                    {d.results.map((c, i) => (
                      <li key={i} className='flex gap-3 text-slate-700 text-[16px] leading-[1.75] font-body'>
                        <div className='w-6 h-6 rounded-full bg-brand/15 flex items-center justify-center shrink-0 mt-0.5'>
                          <Check className='w-3.5 h-3.5 text-brand' />
                        </div>
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Tags */}
              <div className='mt-12 pt-8 border-t border-slate-200 flex flex-wrap gap-2'>
                {cs.tags.map((t) => (
                  <span key={t} className='px-3 py-1 rounded-full bg-slate-100 border border-slate-200 text-[12px] text-slate-700'>
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </article>

          <div className='mt-10 flex items-center justify-between'>
            <Link to='/case-studies' className='inline-flex items-center gap-2 text-brand hover:text-white text-sm font-medium' data-testid='case-study-back'>
              <ArrowLeft className='w-4 h-4' /> All case studies
            </Link>
            <Link to='/contact' className='btn-primary text-white text-sm font-semibold px-5 py-2.5 rounded-full inline-flex items-center gap-2' data-testid='case-study-cta'>
              Start a Program Like This <ArrowRight className='w-4 h-4' />
            </Link>
          </div>
        </div>
      </section>

      {/* Related case studies */}
      {related.length > 0 && (
        <section className='py-16 border-t border-brand/10'>
          <div className='max-w-6xl mx-auto px-6'>
            <h3 className='font-heading text-2xl md:text-3xl font-bold text-white mb-8'>More case studies</h3>
            <div className='grid md:grid-cols-3 gap-6'>
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/case-studies/${r.slug}`}
                  className='glass-card rounded-2xl overflow-hidden group'
                  data-testid={`related-case-study-${r.slug}`}
                >
                  <div className='relative aspect-[16/9] overflow-hidden'>
                    <img src={r.image} alt={r.title} className='w-full h-full object-cover transition-transform duration-700 group-hover:scale-105' />
                    <div className='absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-white/85 text-[10.5px] tracking-wider uppercase font-accent'>{r.industry}</div>
                  </div>
                  <div className='p-5'>
                    <h4 className='font-heading text-[15px] font-bold text-white leading-snug group-hover:text-brand transition-colors'>{r.title}</h4>
                    <div className='mt-2 text-brand text-sm font-medium'>{r.metric}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
